import BlockHeading from '@ui-kit/components/blockHeading';
import React, { useEffect, useState } from 'react';
import { LadderPlayer, ILadderService } from '../../model/interfaces';
import styles from './top20.module.scss';
import PlayerRow from '../common/playerRow/playerRow';
import classNames from 'classnames';
import { ReactComponent as CalcIcon } from '../../assets/calc.svg';
import { ReactComponent as ChallengeIcon } from '../../assets/swords.svg';
import Tooltip from '@ui-kit/components/tooltip';
import { Loader } from '@ui-kit/components/loader';
import Empty from '@ui-kit/components/empty';
import { t, useSubject } from '@base/utils/helpers';
import CalculatedRankOverlay from './calculatedRankOverlay/calculatedRankOverlay';
import ChallengeOverlay from './challengeOverlay/challengeOverlay';
import LastMonthWinners from '../lastMonthWinners/lastMonthWinners';
import { useAsyncData } from '@utils/hooks';
import { PlayerStatistics, IUserService } from '@base/features/user/model/interfaces';
import { container } from 'tsyringe';
import PopoverPlayerStastisticInfo from '@base/features/playerStastisticInfo/popoverPlayerStastisticInfo/popoverPlayerStastisticInfo';
import { IPlayersOnlineRepository } from '@base/features/player/model/interfaces';
import { ReactComponent as QuestionMark } from '@base/features/ladder/assets/question_mark.svg';

export default function Top20(props: { ladderService: ILadderService; myRank?: number }) {
  const now = new Date();
  let date;
  if (now.getMonth() === 11) {
    date = new Date(now.getFullYear() + 1, 0, 1);
  } else {
    date = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  }

  return (
    <div className={styles.top20}>
      <BlockHeading className={styles.heading}>
        <span>{t('ladder_top_20')}</span>
        <div>
          <Tooltip
            overlay={`Monthly $Ticket Prizes awarded to Top Ten players on ${date.toLocaleString('en', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}, 12:01 PM EST`}
            placement="bottom"
          >
            <QuestionMark className={styles.question} />
          </Tooltip>
          <LastMonthWinners ladderService={props.ladderService} />
        </div>
      </BlockHeading>
      <Top20Content myRank={props.myRank} ladderService={props.ladderService} />
    </div>
  );
}

function Top20Content(props: { myRank?: number; ladderService: ILadderService }) {
  const { data: topPlayers, loading } = useAsyncData(() => props.ladderService.getTopPlayers());

  const playersOnlineRepository: IPlayersOnlineRepository = container.resolve('playersOnlineRepository');
  const change = useSubject(playersOnlineRepository.onInOutPlayer);
  const userService: IUserService = container.resolve('userService');

  const [onlinePlayersIds, setOnlinePlayersIds] = useState<Set<string>>();
  const [statPlayers, setStatPlayers] = useState<PlayerStatistics[]>();

  useEffect(() => {
    setOnlinePlayersIds(
      new Set(Object.values(playersOnlineRepository.getPlayersOnline()).map((p) => p.userId || ''))
    );
  }, [change, playersOnlineRepository]);

  useEffect(() => {
    (async function () {
      try {
        if (!topPlayers) return;
        const findPlayers = await Promise.all(topPlayers.map(({ id }) => userService.getPlayerStats(id)));
        setStatPlayers(findPlayers);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [topPlayers]);

  if (loading) return <Loader />;
  if (!topPlayers) return <Empty>{t('error')}</Empty>;

  return (
    <div className={styles.grid}>
      {statPlayers &&
        topPlayers.map((player: LadderPlayer) => (
          <div className={classNames(styles.row, player.rank < 11 ? styles.bordered : '')} key={player.id}>
            <PopoverPlayerStastisticInfo
              placement="left"
              key={player.id}
              player={statPlayers.find((p) => p.UserID === player.id) as PlayerStatistics}
            >
              <div>
                <PlayerRow
                  name={player.userName}
                  avatar={player.avatar}
                  rank={player.rank}
                  online={onlinePlayersIds?.has(player.id)}
                />
              </div>
            </PopoverPlayerStastisticInfo>
            <aside>
              <Tooltip overlay={<CalculatedRankOverlay myRank={props.myRank} hisRank={player.rank} />}>
                <CalcIcon className={styles.calc} />
              </Tooltip>
              <Tooltip
                overlay={<ChallengeOverlay myRank={props.myRank} opponent={{ ...player }} />}
                trigger={['click']}
              >
                <ChallengeIcon className={styles.calc} />
              </Tooltip>
            </aside>
          </div>
        ))}
    </div>
  );
}
